import React from 'react';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';

import Table from 'components/customTable';

import classes from './genderOverTime.module.css';

const ReactTooltip = dynamic(() => import('react-tooltip'), { ssr: false });

const legends = ['Male', 'Female', 'Unknown'];

const COLOR_CODES = ['#26A69A', '#5C6BC0', '#AB47BC'];

const propTypes = {
  jobTitle: PropTypes.string.isRequired,
  genderOverTime: PropTypes.arrayOf(
    PropTypes.shape({
      Male: PropTypes.number,
      Female: PropTypes.number,
      Unknown: PropTypes.number,
      year: PropTypes.number,
    })
  ).isRequired,
};

const GenderOverTime = ({ jobTitle, genderOverTime, genderOverTimeContentBlurb }) => {
  const tableData = (genderOverTime ?? []).map((x) => ({
    ...x,
    MaleDisplay: (100 * x.Male).toFixed(2) + '%',
    FemaleDisplay: (100 * x.Female).toFixed(2) + '%',
  }));

  const index =
    genderOverTime && genderOverTime.length && genderOverTime.findIndex((gender) => gender.Unknown);
  const isLastElementEven = genderOverTime[genderOverTime.length - 1]?.year % 2 === 0;

  const isUnknown = index !== -1;

  const generateYLabels = () => {
    const labelsCount = 5;
    const percentageStep = 100 / labelsCount;
    const yLabels = [];

    for (let i = 5; i >= 0; i--) {
      yLabels.push({ label: i * percentageStep });
    }

    return yLabels;
  };

  const yLabels = generateYLabels();

  const heightOfXLabel = (male) => {
    const maleColumnHeight = Number(male * 100).toFixed(1);
    const femaleColumnHeight = 100 - maleColumnHeight;

    return {
      Male: `${maleColumnHeight}%`,
      Female: `${femaleColumnHeight}%`,
    };
  };

  return (
    <section id="gender-over-time" className={classes['container']}>
      <div className="z-mt-68" />

      <h3 className="cmpSectionh4 z-mb-24 text-capitalize">{`${jobTitle} gender ratio over time`}</h3>

      {genderOverTimeContentBlurb && genderOverTimeContentBlurb.content && (
        <div
          className="z-mb-24"
          dangerouslySetInnerHTML={{ __html: genderOverTimeContentBlurb.content }}
        />
      )}

      <div className={classes['bar-chart']}>
        <div
          className={classNames(
            'd-flex flex-column flex-md-row justify-content-start justify-content-md-center',
            classes['legends']
          )}
        >
          {legends.slice(0, isUnknown ? legends.length : 2).map((legend, idx) => (
            <div key={legend} className={classes['legend']}>
              <div className={classes['circle']} style={{ background: COLOR_CODES[idx] }} />
              <span className="openSansBlack">{legend}</span>
            </div>
          ))}
        </div>
        <div className={classes['grid']}>
          <div className={classNames('d-flex flex-column', classes['y-labels'])}>
            {yLabels.map(({ label }) => (
              <div key={label} className={classes['y-label']}>
                <div className={classNames('lightTextStyle2', classes['label'])}>
                  {label.toFixed(0)}%
                </div>
                <div className={classes['grid-line']} />
              </div>
            ))}
          </div>
          <div className={classNames('d-flex', classes['x-labels'])}>
            {genderOverTime.map(({ year, Male, Female, Unknown }, idx) => {
              const heightOfXLabels = heightOfXLabel(Male);

              return (
                <div
                  key={year + idx}
                  className={classNames(
                    `${
                      (isLastElementEven ? year % 2 === 0 : year % 2 !== 0) ? 'd-flex' : 'd-none'
                    } d-md-flex flex-column align-items-center`,
                    classes['x-label']
                  )}
                >
                  <div className={classNames('d-flex flex-column', classes['bar'])}>
                    <div
                      data-tip={`<div><b>${year}</b></div><div>Male: <strong>${(100 * Male).toFixed(
                        2
                      )}%</strong></div><div>Female: <strong>${(100 * Female).toFixed(
                        2
                      )}%</strong></div>`}
                      data-place="top"
                      style={{ height: heightOfXLabels.Female, background: COLOR_CODES[1] }}
                      className={classNames({ [classes.female]: Unknown })}
                    />
                    <div
                      data-tip={`<div><b>${year}</b></div><div>Male: <strong>${(100 * Male).toFixed(
                        2
                      )}%</strong></div><div>Female: <strong>${(100 * Female).toFixed(
                        2
                      )}%</strong></div>`}
                      data-place="bottom"
                      style={{ height: heightOfXLabels.Male, background: COLOR_CODES[0] }}
                      className={classes['male']}
                    />
                  </div>
                  <div className={classNames('mt-2 lightTextStyle2', classes['label'])}>{year}</div>
                </div>
              );
            })}
          </div>
          <ReactTooltip html />
        </div>
      </div>
      <div className="mb-0 z-mt-68">
        <Table
          columnConfig={{
            year: {
              columnName: 'Year',
              defaultSortDirection: 'desc',
            },
            MaleDisplay: {
              columnName: 'Male',
              customSortByField: 'Male',
            },
            FemaleDisplay: {
              columnName: 'Female',
              customSortByField: 'Female',
            },
          }}
          bodyData={tableData}
          defaultRowsDisplay={99}
          tableHeaderTitleContent={
            <h3 className="zp-title-h3 text-capitalize z-mb-24">{`${jobTitle} gender ratio by year`}</h3>
          }
        />
      </div>
    </section>
  );
};

GenderOverTime.propTypes = propTypes;

export default GenderOverTime;
